import { useCallback, useState } from 'react';
import type { GeoPoint } from './geoPoint';

type State =
  | { status: 'idle' }
  | { status: 'asking' }
  | { status: 'granted'; position: GeoPoint }
  | { status: 'denied' }
  | { status: 'unavailable' };

/**
 * A single location fix, on demand.
 *
 * The canvas is specific about the contract: "A localização é lida uma única vez, com
 * permissão explícita. Recusar não bloqueia o fluxo — a busca continua." So this never
 * asks on mount, never watches, and a refusal is a normal outcome rather than an error.
 */
export function useGeolocation() {
  const [state, setState] = useState<State>({ status: 'idle' });

  const request = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setState({ status: 'unavailable' });
      return;
    }

    setState({ status: 'asking' });
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setState({
          status: 'granted',
          position: { longitude: pos.coords.longitude, latitude: pos.coords.latitude },
        }),
      () => setState({ status: 'denied' }),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60_000 },
    );
  }, []);

  return { ...state, request };
}
